import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading, SidePanel } from 'design-system-web';
import { Alert } from 'components/lds';
import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { Channel } from 'application-types';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import {
  ALL_ISSUE_TYPES_VALUE,
  buildIssueTypeItems,
  findSelectedCategory,
} from './coverageHoursPanelUtils';
import { useCoverageHoursOptions } from './useCoverageHoursOptions';
import { CoverageHoursForm } from './CoverageHoursForm';
import { CoverageHoursPanelFooter } from './CoverageHoursPanelFooter';
import { useSaveCoverageHours } from './useSaveCoverageHours';
import {
  INITIAL_PANEL_STATE,
  cancelClose,
  changeCategory,
  changeIssueType,
  changeTeamFormConflict,
  closePanel,
  discardTeam,
  failSave,
  panelReducer,
  requestClose,
  saveTeam,
  settleSave,
  startSave,
} from './coverageHoursPanelReducer';

const TRANSLATION_KEYS: Record<'title' | 'saveError' | 'loadError', TranslationKeys> =
  Object.freeze({
    title: 'web.admin.serviceRequest.settings.coverageHours.create.title',
    saveError: 'web.admin.serviceRequest.settings.coverageHours.create.saveError',
    loadError: 'web.admin.serviceRequest.settings.coverageHours.error.load',
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  channel?: Channel;
  categories: WorkOrderModuleCategoriesSettings[];
  onCreateSuccess: () => void;
};

export function CoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  channel,
  categories,
  onCreateSuccess,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const [
    {
      categoryValue,
      issueTypeValue,
      savedTeams,
      hasTeamFormConflict,
      isConfirmingClose,
      saveError,
      isSaving,
    },
    dispatch,
  ] = useReducer(panelReducer, INITIAL_PANEL_STATE);
  const {
    categories: categoryOptions,
    issueTypes: issueTypeOptions,
    status: optionsStatus,
    error: optionsError,
    fetchCoverageHoursOptions,
  } = useCoverageHoursOptions(channelId);

  const selectedCategory = useMemo(
    () => findSelectedCategory(categories, categoryValue),
    [categories, categoryValue]
  );
  const { saveCoverageHours } = useSaveCoverageHours(channelId, channel);

  const hasUnsavedChanges = savedTeams.length > 0;
  const canSave =
    Boolean(categoryValue) &&
    savedTeams.length > 0 &&
    !isSaving &&
    optionsStatus === 'success' &&
    !hasTeamFormConflict;

  // Must stay stable: a new identity re-fires the team form's effect every
  // render, and that effect's cleanup reports the opposite value, so the two
  // alternate forever. Do not inline this.
  const handleConflictChange = useCallback(
    (hasConflict: boolean) => dispatch(changeTeamFormConflict(hasConflict)),
    []
  );

  useEffect(() => {
    if (!isOpen) {
      dispatch(cancelClose());
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Errors surface through optionsError; this call just triggers the fetch.
      fetchCoverageHoursOptions().catch(() => {});
    }
  }, [isOpen, fetchCoverageHoursOptions]);

  const handleCategoryChange = (value: string) => {
    const newSelectedCategory = findSelectedCategory(categories, value);
    const newIssueTypeItems = buildIssueTypeItems(
      newSelectedCategory,
      issueTypeOptions,
      t
    );

    dispatch(
      changeCategory(
        value,
        newIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE
      )
    );
  };

  const handleClose = () => {
    dispatch(closePanel());
    onClose();
  };

  const handleSaveTeam = (entry: TeamCoverageEntry) => {
    dispatch(saveTeam(entry));
  };

  const handleDiscardTeam = (id: string) => {
    dispatch(discardTeam(id));
  };

  const handleOnCancel = () => {
    if (hasUnsavedChanges) {
      dispatch(requestClose());
    } else {
      handleClose();
    }
  };

  const handleSave = async () => {
    if (!categoryValue) {
      return;
    }

    dispatch(startSave());

    try {
      await saveCoverageHours({
        categoryValue,
        issueTypeValue,
        selectedCategory,
        issueTypeOptions,
        savedTeams,
      });

      onCreateSuccess();
      handleClose();
    } catch {
      dispatch(failSave());

      try {
        // Covers a partial success (issues created, then the None call
        // failed) - refetch so a retry sees what's already used.
        const freshOptions = await fetchCoverageHoursOptions();

        const freshIssueTypeItems = buildIssueTypeItems(
          selectedCategory,
          freshOptions.issueTypes,
          t
        );

        if (!freshIssueTypeItems.some(item => item.value === issueTypeValue)) {
          dispatch(
            changeIssueType(
              freshIssueTypeItems[0]?.value ?? ALL_ISSUE_TYPES_VALUE
            )
          );
        }
      } catch {
        // The save's own error is already shown above; nothing further
        // to reconcile without fresh data from this second failure.
      }
    } finally {
      dispatch(settleSave());
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={isSaving ? undefined : handleClose}
      header={t(TRANSLATION_KEYS.title)}
      size="medium"
      backdropEnabled
      footerActions={
        <CoverageHoursPanelFooter
          isConfirmingClose={isConfirmingClose}
          onDiscardChanges={handleClose}
          onKeepEditing={() => dispatch(cancelClose())}
          onCancel={handleOnCancel}
          onSave={handleSave}
          isSaving={isSaving}
          canSave={canSave}
        />
      }
    >
      {saveError && (
        <Alert type="error">{t(TRANSLATION_KEYS.saveError)}</Alert>
      )}
      {optionsError && (
        <Alert type="error">{t(TRANSLATION_KEYS.loadError)}</Alert>
      )}
      {optionsStatus === 'loading' && <Loading />}
      {optionsStatus === 'success' && (
        <CoverageHoursForm
          categories={categories}
          categoryOptions={categoryOptions}
          issueTypeOptions={issueTypeOptions}
          categoryValue={categoryValue}
          onCategoryChange={handleCategoryChange}
          issueTypeValue={issueTypeValue}
          onIssueTypeChange={value =>
            dispatch(changeIssueType(value))
          }
          assignableTeams={assignableTeams}
          savedTeams={savedTeams}
          onSaveTeam={handleSaveTeam}
          onDiscardTeam={handleDiscardTeam}
          channelId={channelId}
          onConflictChange={handleConflictChange}
        />
      )}
    </SidePanel>
  );
}
